import { EventObject, Subscription } from 'xstate'

import { GameState, RunnerActor } from '@/state/RunnerStateMachine'
import { clamp01 } from '@/webgl/math'
import GameEvents from '@/webgl/runner/core/GameEvents'
import { BonusType } from '@/webgl/runner/entities/BonusEntity'
import { CollectStatus } from '@/webgl/runner/entities/CollectibleEntity'
import { SequenceCompletion } from '@/webgl/runner/scenarios/SequenceCompletion'
import { SequenceAlternativeMode, SequenceView } from '@/webgl/runner/scenarios/SequenceConfig.types'

export default class GameStateEntity {
  actor: RunnerActor

  value: GameState
  subscribption: Subscription | null = null

  constructor(readonly events: GameEvents) {}

  setActor(actor: RunnerActor) {
    this.actor = actor
    this.value = this.actor.getSnapshot().context.game
    this.subscribption = this.actor.on('player_move', this.onPlayerMove)
  }

  onPlayerMove = (evt: EventObject) => {
    this.events.PlayerInput.emit({
      direction: (evt as unknown as { direction: number }).direction,
    })
  }

  dispose() {
    this.subscribption?.unsubscribe()
    this.subscribption = null
  }

  notify() {
    this.actor?.send({ type: 'GAME_STATE', value: this.value })
  }

  setLane(lane: number) {
    this.value.currentLane = lane
    this.notify()
  }

  setPopin(popin: string) {
    this.value.popin = popin
    this.notify()
  }

  setTooltip(tooltip: string | null) {
    this.value.tooltip = tooltip
    this.notify()
  }

  updateCompletionView(view: SequenceView) {
    this.value.completion.view = view
  }

  setCompletion(completion: SequenceCompletion) {
    this.value.completion = completion
    this.notify()
  }

  getCurrentCompletionBlock() {
    if (!this.value.completion) return null
    return this.value.completion.blocks[this.value.completion.currentBlock]
  }

  collectItem(item: string) {
    const idx = this.value.completion.currentBlock
    const block = this.value.completion.blocks[idx]

    if (block.collected.includes(item)) return

    block.collected.push(item)

    this.goToNextBlock()
    this.notify()
  }

  failCurrentGoal() {
    const currentItem = this.getCurrentGoal()
    const idx = this.value.completion.currentBlock
    const block = this.value.completion.blocks[idx]
    block.failed.push(currentItem)
    block.collected.push(currentItem)
    this.goToNextBlock()
    this.notify()
  }

  goToNextBlock() {
    const idx = this.value.completion.currentBlock
    const block = this.value.completion.blocks[idx]
    if (block.collected.length == block.goal.length) {
      block.status = block.failed.length == 0 ? CollectStatus.Succeed : CollectStatus.Failed
      if (idx < this.value.completion.blocks.length - 1) {
        this.value.completion.currentBlock++
      }
    }
  }

  setPaused(paused: boolean) {
    this.value.paused = paused
  }

  setJauge(value: number) {
    this.value.jauge = clamp01(value)
    this.notify()
  }

  alterJauge(value: number) {
    this.setJauge(this.value.jauge + value)
  }

  setTurningPoint(turningPoint: string) {
    this.value.turningPoint = turningPoint
    this.notify()
  }

  setObstacle(obstacle: string) {
    this.value.obstacle = obstacle
    this.notify()
  }

  setCurrentSegment(id: string, type: string) {
    this.value.currentSegment = id
    this.value.currentSegmentType = type
    this.notify()
  }

  setCollectStatus(status: CollectStatus) {
    this.value.collectStatus = status
    this.notify()
  }

  getCurrentGoal(): string {
    const block = this.getCurrentCompletionBlock()
    if (!block) return null
    return block.goal[block.collected.length]
  }

  getCurrentGoals(): string[] {
    const block = this.getCurrentCompletionBlock()
    if (!block) return null
    return block.goal
  }

  getAlternativeMode(): SequenceAlternativeMode {
    return this.value.completion ? this.value.completion.alternativeMode : null
  }

  addActiveBonus(bonus: BonusType) {
    if (this.value.activeBonuses.indexOf(bonus) != -1) return
    this.value.activeBonuses.push(bonus)
    this.notify()
  }

  removeActiveBonus(bonus: BonusType) {
    const idx = this.value.activeBonuses.indexOf(bonus)
    if (idx == -1) return
    this.value.activeBonuses.splice(idx, 1)
    this.notify()
  }

  setLoadProgress(value: number) {
    this.value.loadingProgress = value
    this.notify()
  }
}
